/**
 * zeroMonth — the 0 as a month, and the arithmetic behind it.
 *
 * FACE 2 OF THE SIGNATURE OBJECT. Face 1 (zeroDay.ts) is a day: 24 hours, one
 * capability at each stop. This is the same mark holding the same argument one
 * scale up — the full circle is ONE Prest0n's monthly allocation, and the arcs
 * are what a real month of work spends it on.
 *
 * THE REASON THIS SHAPE IS HONEST AND NOT A CONCEIT. TYPICAL_MONTH's five rows
 * sum to exactly MONTHLY_WU_PER_PRESTON:
 *
 *     25 memos       x 8 = 200
 *    120 documents   x 3 = 360
 *    150 translations x 3 = 450
 *    350 files       x 2 = 700
 *    490 tasks       x 1 = 490
 *                        -----
 *                        2,200   = one Prest0n's month, exactly
 *
 * Nothing was authored to make that land. The worked example and the allocation
 * are the same number because the worked example was built from the allocation,
 * so the brand mark and the price genuinely are one object. If that ever stops
 * being true this module THROWS at import rather than drawing a circle that
 * quietly is not a month — the same discipline zeroDay.ts applies to the rate
 * card, applied to the allocation. A silently-wrong pricing visual on the
 * pricing page is the exact failure this whole pass exists to remove.
 *
 * THE CIRCLE IS ALWAYS 2,200 — never the current total. When a visitor dials a
 * category up, the arcs do not rescale to keep filling the ring; they fill more
 * of a fixed month and the remainder shrinks. Rescaling would make every
 * arrangement look identical and destroy the only thing the picture is for.
 * Past 2,200 the ring is simply full and the overflow is stated in words, which
 * IS the product argument: that is what a second Prest0n is for.
 */

import {
  TYPICAL_MONTH,
  MONTHLY_WU_PER_PRESTON,
  typicalMonthLineWu,
  singleProductCeiling,
} from '../../config/workUnits';

/** The full circle. One Prest0n, one month. */
export const MONTH_TOTAL_WU = MONTHLY_WU_PER_PRESTON;

export type MonthKey = (typeof TYPICAL_MONTH)[number]['key'];

/** How many of each kind of work — the thing a visitor changes. */
export type MonthCounts = Record<MonthKey, number>;

export interface MonthArc {
  key: MonthKey;
  /** The shipped plural, e.g. "drafted documents". Never re-worded here. */
  item: string;
  count: number;
  /** The published rate for one of them. */
  wuEach: number;
  /** count x wuEach. */
  wu: number;
  /** Share of the MONTH (not of the current total) — 0..1, may exceed 1. */
  share: number;
  /** Degrees, 12 o'clock straight up, clockwise — zeroDay.ts's convention. */
  startAngle: number;
  endAngle: number;
  /** The most of THIS product one month can hold, from the SSOT. */
  max: number;
}

// ── The invariant, checked once at import ────────────────────────────────────
//
// Not a test — a load-bearing precondition. A test proves it in CI; this makes
// the page fail loudly rather than render a ring whose arcs no longer add up to
// the month it claims to be.
const TYPICAL_TOTAL = TYPICAL_MONTH.reduce((sum, row) => sum + typicalMonthLineWu(row), 0);
if (TYPICAL_TOTAL !== MONTH_TOTAL_WU) {
  throw new Error(
    `zeroMonth: TYPICAL_MONTH sums to ${TYPICAL_TOTAL} pr0 but one Prest0n's month is `
      + `${MONTH_TOTAL_WU}. The month ring draws the worked example as a share of the `
      + 'allocation, so the two must be the same number. Fix TYPICAL_MONTH in '
      + 'src/config/workUnits.ts (or the allocation), not this file.',
  );
}

/** 12 o'clock is straight up; the month runs clockwise. Mirrors hourToAngle. */
export function wuToAngle(wu: number): number {
  return (wu / MONTH_TOTAL_WU) * 360 - 90;
}

/** The worked example, as the starting position of the ring. */
export function typicalCounts(): MonthCounts {
  return TYPICAL_MONTH.reduce(
    (acc, row) => ({ ...acc, [row.key]: row.count }),
    {} as MonthCounts,
  );
}

/** What an arrangement costs. */
export function monthTotalWu(counts: MonthCounts): number {
  return TYPICAL_MONTH.reduce(
    (sum, row) => sum + (counts[row.key] ?? 0) * row.wuEach,
    0,
  );
}

/**
 * What is left of the month — negative once the arrangement needs more than one
 * Prest0n. Callers render the sign; this only does the arithmetic.
 */
export function remainingWu(counts: MonthCounts): number {
  return MONTH_TOTAL_WU - monthTotalWu(counts);
}

/**
 * The arcs, in reading order, laid end to end from 12 o'clock.
 *
 * Angles are NOT clamped to the circle: an arrangement over the allocation
 * returns end angles past 270°, and the component decides how to draw that
 * (it stops at full and says so in words). Clamping here would hide the
 * overflow from the one caller that needs to know about it.
 */
export function monthArcs(counts: MonthCounts): MonthArc[] {
  let cursor = 0;
  return TYPICAL_MONTH.map((row) => {
    const count = Math.max(0, counts[row.key] ?? 0);
    const wu = count * row.wuEach;
    const startAngle = wuToAngle(cursor);
    cursor += wu;
    return {
      key: row.key,
      item: row.item,
      count,
      wuEach: row.wuEach,
      wu,
      share: wu / MONTH_TOTAL_WU,
      startAngle,
      endAngle: wuToAngle(cursor),
      max: singleProductCeiling(row.wuEach),
    };
  });
}

/**
 * The one sentence under the ring. Kept here rather than in the component so
 * the words and the arithmetic cannot disagree — the failure mode is a ring
 * showing one thing and a caption claiming another.
 */
export function monthVerdict(counts: MonthCounts): {
  totalWu: number;
  remaining: number;
  /** How many Prest0ns this arrangement actually needs. */
  seatsNeeded: number;
  over: boolean;
} {
  const totalWu = monthTotalWu(counts);
  const remaining = MONTH_TOTAL_WU - totalWu;
  return {
    totalWu,
    remaining,
    seatsNeeded: Math.max(1, Math.ceil(totalWu / MONTH_TOTAL_WU)),
    over: totalWu > MONTH_TOTAL_WU,
  };
}
